import React, { useEffect, useState } from 'react';
import { Radio, Segmented } from 'antd';
import { useFormContext } from 'react-hook-form';
import BWAccordion from 'components/shared/BWAccordion';
import FormItem from 'components/shared/BWFormControl/FormItem';
import FormNumber from 'components/shared/BWFormControl/FormNumber';
import { OFFER_TYPE } from 'pages/PromotionOffers/utils/constants';
import { offerTypes, typePromotionOffers } from 'pages/PromotionOffers/utils/helpers';
import PromotionsProductTable from './PromotionProductTable';
import PromotionProductModal from './PromotionProductModal';
import './PromotionOffersForm.scss';

const SHIPPING_TYPE = { FREE: 1, MONEYTRANFER: 2, PERCENTTRANFER: 3 };

const PromotionOffersType = ({ disabled }) => {
  const [openModalGift, setOpenModalGift] = useState(false);
  const { watch, setValue, register } = useFormContext();
  const offerType = Number(watch('offer_type'));
  const typeValue = Number(watch('type_value')) || 1;
  const shippingType = Number(watch('shipping_promotion'));
  const discountValue = Number(watch('discount_value')) || 0;
  const companyValue = Number(watch('company_value')) || 0;
  const activeOffer = offerTypes.find(({ value }) => value === offerType);

  useEffect(() => {
    register('offer_type');
    register('type_value');
    register('shipping_promotion');
  }, [register]);

  useEffect(() => {
    if (disabled || offerType !== OFFER_TYPE.ISPAYMENTFORM) return;
    setValue('partner_value', Math.max(0, (typeValue === 2 ? 100 : discountValue) - companyValue));
  }, [disabled, offerType, typeValue, discountValue, companyValue, setValue]);

  const handleChangeOfferType = (value) => {
    if (disabled || value === offerType) return;
    setValue('offer_type', value, { shouldDirty: true });
    setValue('discount_value', 0);
    setValue('company_value', 0);
    setValue('partner_value', 0);
    if (value === OFFER_TYPE.TRANSPORT && !shippingType) setValue('shipping_promotion', SHIPPING_TYPE.FREE);
    if (value === OFFER_TYPE.ISPAYMENTFORM) setValue('type_value', typeValue);
  };

  const numberField = (field, label, unit = 'VNĐ', props = {}) => (
    <FormItem label={label} disabled={disabled}>
      <FormNumber field={field} addonAfter={unit} min={0} controls={false} disabled={disabled} {...props} />
    </FormItem>
  );

  const renderOffer = () => {
    switch (offerType) {
      case OFFER_TYPE.GIFT:
        return (
          <>
            <p className='bw_offer_hint'>Có thể chọn nhiều sản phẩm quà. Khi tạo đơn hàng, người dùng được chọn đồng thời nhiều quà trong danh sách này.</p>
            <PromotionsProductTable
              fieldProduct='list_offer_gifts'
              contentCreate='Chọn nhiều quà tặng'
              noLoadData
              showDefaultQuantity
              disabled={disabled}
              handleOpenModalGift={() => setOpenModalGift(true)}
            />
          </>
        );
      case OFFER_TYPE.TRANSPORT:
        return (
          <>
            <Radio.Group
              className='bw_offer_shipping'
              aria-label='Hình thức hỗ trợ vận chuyển'
              disabled={disabled}
              value={shippingType}
              onChange={(e) => setValue('shipping_promotion', e.target.value, { shouldDirty: true })}>
              <Radio value={SHIPPING_TYPE.FREE}>Miễn phí vận chuyển</Radio>
              <Radio value={SHIPPING_TYPE.MONEYTRANFER}>Hỗ trợ theo số tiền</Radio>
              <Radio value={SHIPPING_TYPE.PERCENTTRANFER}>Hỗ trợ theo phần trăm</Radio>
            </Radio.Group>
            <div className='bw_offer_values'>
              {shippingType === SHIPPING_TYPE.MONEYTRANFER && numberField('discount_shipping_fee', 'Số tiền hỗ trợ')}
              {shippingType === SHIPPING_TYPE.PERCENTTRANFER && (
                <>
                  {numberField('percent_shipping_fee', 'Tỷ lệ hỗ trợ', '%', { max: 100 })}
                  {numberField('discount_max', 'Giảm tối đa')}
                </>
              )}
            </div>
          </>
        );
      case OFFER_TYPE.PERCENT:
        return (
          <div className='bw_offer_values'>
            {numberField('discount_value', 'Tỷ lệ giảm', '%', { max: 100 })}
            {numberField('max_value_reduce', 'Giảm tối đa')}
            {numberField('min_total_money', 'Giá trị đơn hàng từ')}
            {numberField('max_total_money', 'Giá trị đơn hàng đến')}
          </div>
        );
      case OFFER_TYPE.ISPAYMENTFORM:
        return (
          <>
            <FormItem label='Đơn vị phân bổ hỗ trợ' disabled={disabled}>
              <Segmented
                aria-label='Đơn vị phân bổ hỗ trợ'
                options={typePromotionOffers}
                disabled={disabled}
                value={typeValue}
                onChange={(value) => {
                  setValue('type_value', value, { shouldDirty: true });
                  setValue('company_value', 0, { shouldDirty: true });
                }}
              />
            </FormItem>
            <div className='bw_offer_values'>
              {numberField('discount_value', 'Giá trị ưu đãi')}
              {numberField('company_value', 'Công ty hỗ trợ', typeValue === 2 ? '%' : 'VNĐ', {
                max: typeValue === 2 ? 100 : discountValue,
                validation: {
                  validate: (value) => Number(value || 0) <= (typeValue === 2 ? 100 : discountValue) || 'Phần công ty hỗ trợ vượt quá giá trị ưu đãi.',
                },
              })}
              {numberField('partner_value', 'Đơn vị chấp nhận thanh toán hỗ trợ', typeValue === 2 ? '%' : 'VNĐ', { disabled: true })}
            </div>
          </>
        );
      case OFFER_TYPE.DIRECT:
      case OFFER_TYPE.HARD:
        return <div className='bw_offer_values'>{numberField('discount_value', offerType === OFFER_TYPE.HARD ? 'Giá bán đồng giá' : 'Số tiền giảm')}</div>;
      default:
        return null;
    }
  };

  return (
    <BWAccordion title='Ưu đãi khuyến mại'>
      <div className='bw_offer_type_form'>
        <Radio.Group
          className='bw_offer_type_choices'
          aria-label='Loại ưu đãi khuyến mại'
          value={offerType}
          disabled={disabled}
          onChange={(e) => handleChangeOfferType(e.target.value)}>
          {offerTypes.map(({ value, label }) => <Radio key={value} value={value}>{label}</Radio>)}
        </Radio.Group>
        {activeOffer && (
          <section className='bw_offer_editor' aria-label={activeOffer.label}>
            <h4>{activeOffer.label}</h4>
            {renderOffer()}
          </section>
        )}
      </div>
      {openModalGift && !disabled && (
        <PromotionProductModal fieldProduct='list_offer_gifts' title='Chọn sản phẩm quà tặng' onClose={() => setOpenModalGift(false)} />
      )}
    </BWAccordion>
  );
};

export default PromotionOffersType;
